/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */

/**
 *
 *
 * @author Yakov Gushchin
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.0.1
 */

import { Fragment, memo, useEffect, useRef } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import moment from 'moment';
import Carousel from 'react-multi-carousel';
import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@/main/pages/PageAuth/selectors/authSelectors';
import { LinkBlock, Skeleton, TextBlock } from '@components/common';
import { LayoutStyled } from '@components/common/Block/Layout';
import { ESkeletonKind } from '@components/common/Skeleton/skeletonTypes';
import { pushGTMAnalytic } from '@modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import { EGTMWidgetCostsArrowType } from '@modules/analytics/GoogleTagManager/GTMOperations/GTMWidgetCostsOperations';
import type { IGTMPushAnalyticPayload } from '@modules/analytics/GoogleTagManager/types/GTMActionTypes';
import initTranslationCommon from '@modules/internationalization/constants/common';
import initTranslationPageDashboard from '@modules/internationalization/constants/pages/dashboard/dashboard';
import type ICommonTranslation from '@modules/internationalization/types/common';
import type IPageDashboardTranslation from '@modules/internationalization/types/pages/dashboard/dashboard';
import { makeSelectLocalStorageSelectedAccount } from '@modules/storage/storageSelectors';
import { EFormatData } from '@pages/PageDashboard/types/widgetTypes';
import type { IRootReducer } from '@reducers/rootReducer';
import { CLIENT_ID, RoutePath } from '@routes/constants/routesConstants';

import { WidgetSkeletonChart } from '../../constants/widgetSkeletonContants';
import { WidgetDateRangeStyled } from '../../styled/WidgetDateRangeStyled';
import { WidgetSubtitleStyled, WidgetTitleStyled } from '../../styled/WidgetStyled';
import type { IWidgetSearchAccountsItem } from '../../WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import { EWidgetStatus } from '../../widgetTypes';
import { widgetCostsConfiguration } from '../constant/widgetCostsConstants';
import {
    WidgetCostsChartStyled,
    WidgetCostsContainerStyled,
    WidgetCostsHeaderStyled,
    WidgetCostsLinkStyled,
} from '../styled/WidgetCostsStyled';
import type { IWidgetCostsLoadChartPayload, IWidgetCostsRedirectToPaymentsPayload } from '../types/actionsType';
import { widgetCostsLoadChart, widgetCostsRedirectToPayments } from '../widgetCostsActions';
import type { IWidgetCostsChart } from '../widgetCostsReducer';
import {
    makeSelectorWidgetCostsChart,
    makeSelectorWidgetCostsCurrentDateTo,
    makeSelectorWidgetCostsIsLoading,
    makeSelectorWidgetCostsStatus,
} from '../widgetCostsSelectors';
import WidgetCostsChartEmpty from './WidgetCostsChartEmpty';
import WidgetCostsSlider from './WidgetCostsSlider';
import WidgetCostsSliderArrow from './WidgetCostsSliderArrow';

export interface IConnectedProps {
    charts: IWidgetCostsChart;
    currentDateTo: string;
    isLoading: boolean;
    selectedAccount: IWidgetSearchAccountsItem;
    status: EWidgetStatus;
}

export interface IDispatchProps {
    loadChart: (payload: IWidgetCostsLoadChartPayload) => void;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    widgetCostsRedirectToPayments: (payload: IWidgetCostsRedirectToPaymentsPayload) => void;
}

interface IWidgetCostsProps {
    translation: IPageDashboardTranslation & ICommonTranslation;
}

export type TWidgetCosts = IWidgetCostsProps & IConnectedProps & IDispatchProps;

export const WidgetCosts = (props: TWidgetCosts) => {
    const { charts, currentDateTo, isLoading, loadChart, translation, status } = props;

    const isPreliminaryDataFeatureEnabled = useSelector(
        makeSelectFeatureEnabled(FeatureNameEnum.EXPENSES_PRELIMINARY_DATA),
    );

    const carouselRef = useRef<Carousel | null>(null);

    useEffect((): void => {
        loadChart({
            dateFrom: widgetCostsConfiguration.initDataFrom,
            dateTo: widgetCostsConfiguration.initDateTo,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedAccount.id]);

    const onRightArrowClick = (): void => {
        const nextMonth = moment(currentDateTo, widgetCostsConfiguration.dateFormat).add(1, 'month');
        const dateFrom = nextMonth.startOf('month').format(widgetCostsConfiguration.dateFormat);
        const dateTo = nextMonth.endOf('month').format(widgetCostsConfiguration.dateFormat);
        loadChart({ dateFrom: dateFrom, dateTo: dateTo });

        // GTM Analytic
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetCosts.clickArrowWidgetCosts(
                EGTMWidgetCostsArrowType.right,
                `${dateFrom}_${dateTo}`,
            ),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    const onLeftArrowClick = (): void => {
        const nextMonth = moment(currentDateTo, widgetCostsConfiguration.dateFormat).subtract(1, 'month');
        const dateFrom = nextMonth.startOf('month').format(widgetCostsConfiguration.dateFormat);
        const dateTo = nextMonth.endOf('month').format(widgetCostsConfiguration.dateFormat);
        loadChart({ dateFrom: dateFrom, dateTo: dateTo });

        // GTM Analytic
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetCosts.clickArrowWidgetCosts(
                EGTMWidgetCostsArrowType.left,
                `${dateFrom}_${dateTo}`,
            ),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    const getCurrentDate = (): string => {
        const date = moment(currentDateTo, widgetCostsConfiguration.dateFormat);
        const dateFrom = date.startOf('month').format(EFormatData.OnlyDay);
        let dateTo;
        if (date.endOf('month') > moment()) {
            dateTo = moment(new Date()).format(EFormatData.OnlyDay);
        } else {
            dateTo = date.endOf('month').format(EFormatData.OnlyDay);
        }
        const month = translation.calendar.monthDeclension[+date.format(EFormatData.OnlyMonth) - 1];
        const year = date.format(EFormatData.OnlyYear);
        return `${translation.commonWidget.from.toLocaleUpperCase()}
        ${dateFrom} ${translation.commonWidget.to} ${dateTo} ${month} ${year}`;
    };
    const currentDate = getCurrentDate();

    const isLeftArrowDisabled = (): boolean =>
        moment().subtract(widgetCostsConfiguration.chart.yearLimit, 'year').endOf('month').unix() >
        moment(currentDateTo, widgetCostsConfiguration.dateFormat).unix();

    const isRightArrowDisabled = (): boolean =>
        moment().endOf('month').unix() ===
        moment(currentDateTo, widgetCostsConfiguration.dateFormat).endOf('month').unix();

    const getUrlToExpenses = () => {
        const accountId = props.selectedAccount.id.toString();
        const date = moment(currentDateTo, widgetCostsConfiguration.dateFormat).format(EFormatData.YearMonthReverse);
        return `${RoutePath.expensesAnalyzerAccountId.replace(CLIENT_ID, accountId)}?d=${date}`;
    };

    const redirectToPayments = (): void => {
        const date = moment(currentDateTo, widgetCostsConfiguration.dateFormat);
        const dateFrom = date.startOf('month').format(widgetCostsConfiguration.dateFormat);
        const dateTo = date.endOf('month').format(widgetCostsConfiguration.dateFormat);

        // GTM Analytic
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetCosts.clickLinkAllCostsWidgetCosts(`${dateFrom}_${dateTo}`),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    const getEmptyMsg = (): string =>
        moment().endOf('month').unix() ===
        moment(currentDateTo, widgetCostsConfiguration.dateFormat).endOf('month').unix()
            ? translation.widgets.costs.emptyMsg
            : translation.widgets.costs.emptyMsgBefore;

    return (
        <WidgetCostsContainerStyled data-name="widget-container" data-widget="WidgetCosts" isLoading={isLoading}>
            <WidgetCostsHeaderStyled>
                <WidgetTitleStyled>{translation.widgets.costs.title}</WidgetTitleStyled>
                {isPreliminaryDataFeatureEnabled && (
                    <Typography>{translation.widgets.costs.preliminaryData}</Typography>
                )}
                <WidgetSubtitleStyled>
                    {
                        {
                            empty: <WidgetDateRangeStyled>{currentDate}</WidgetDateRangeStyled>,
                            error: null,
                            success: <WidgetDateRangeStyled>{currentDate}</WidgetDateRangeStyled>,
                        }[status]
                    }
                </WidgetSubtitleStyled>
            </WidgetCostsHeaderStyled>
            <Skeleton
                isLoading={isLoading}
                render={() =>
                    WidgetSkeletonChart({
                        multiple: widgetCostsConfiguration.skeleton.multiple,
                        kind: ESkeletonKind.CHART_DONUT,
                    })
                }
            >
                <Fragment>
                    <WidgetCostsSliderArrow
                        carouselRef={carouselRef}
                        isLeftDisabled={isLeftArrowDisabled()}
                        isRightDisabled={isRightArrowDisabled()}
                        onArrowLeftClick={onLeftArrowClick}
                        onArrowRightClick={onRightArrowClick}
                    />
                    <WidgetCostsChartStyled>
                        {
                            {
                                empty: (
                                    <WidgetCostsChartEmpty
                                        currency={translation.currency}
                                        isLegendDisable={true}
                                        isTooltipDisable={true}
                                        total={0}
                                    />
                                ),
                                error: (
                                    <WidgetCostsChartEmpty
                                        currency={translation.currency}
                                        isLegendDisable={true}
                                        isTooltipDisable={true}
                                        isTotalDisable={true}
                                    />
                                ),
                                success: (
                                    <WidgetCostsSlider
                                        carouselRef={carouselRef}
                                        chart={charts[currentDateTo]}
                                        translation={translation}
                                    />
                                ),
                            }[status]
                        }
                    </WidgetCostsChartStyled>
                    {
                        {
                            empty: (
                                <LayoutStyled
                                    className="widget-cost__msg-empty mt-4 t-center"
                                    data-name="empty-message"
                                >
                                    <TextBlock>{getEmptyMsg()}</TextBlock>
                                </LayoutStyled>
                            ),
                            error: (
                                <LayoutStyled className="widget-cost__msg-error mt-4 t-center">
                                    <TextBlock>{translation.widgets.costs.errorMsg}</TextBlock>
                                </LayoutStyled>
                            ),
                            success: (
                                <WidgetCostsLinkStyled>
                                    <LinkBlock url={getUrlToExpenses()} onClick={redirectToPayments}>
                                        {translation.widgets.costs.link}
                                    </LinkBlock>
                                </WidgetCostsLinkStyled>
                            ),
                        }[status]
                    }
                </Fragment>
            </Skeleton>
        </WidgetCostsContainerStyled>
    );
};

WidgetCosts.defaultProps = {
    translation: { ...initTranslationPageDashboard, ...initTranslationCommon },
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    charts: makeSelectorWidgetCostsChart(),
    currentDateTo: makeSelectorWidgetCostsCurrentDateTo(),
    isLoading: makeSelectorWidgetCostsIsLoading(),
    selectedAccount: makeSelectLocalStorageSelectedAccount(),
    status: makeSelectorWidgetCostsStatus(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    loadChart: (payload: IWidgetCostsLoadChartPayload) => dispatch(widgetCostsLoadChart(payload)),
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
    widgetCostsRedirectToPayments: (payload: IWidgetCostsRedirectToPaymentsPayload) =>
        dispatch(widgetCostsRedirectToPayments(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(WidgetCosts));
